<template>
  <div>
    <div class="modal-body">
      <p>Upload a <b>photo taken with your camera</b> and we'll show you the model from the exif data.</p>
      <p>Or <a href="/contact">contact us</a> and we will help you figure it out.</p>

      <form @submit.prevent="submit">
        <div class="custom-file" @drop.prevent="drop" @dragenter="highlight = true" @dragleave="highlight = false">
          <input id='js-camera-image' type="file" class="custom-file-input" @change="submit" />
          <label :class="{ 'custom-file-label': true, 'custom-file-label-highlight': highlight }">Choose or drop a file</label>
          <div v-if="loading" class="custom-file-loading" />
          <div v-if="loading && progress > 0" class="custom-file-progress" :style="{ width: progress + '%' }"></div>
          <div v-if="loading" class="custom-file-progress-message">Upload Progress: {{ progress }}%</div>
        </div>
      </form>

      <div v-if="error" class="error form-error">{{ error }}</div>

      <div v-if="camera" class="camera-result card" style="margin-top: 20px">
        <p>Your camera is a
          <b>{{ camera.brand }} {{ camera.model_formatted }}</b>
        </p>
      </div>
    </div>

    <div class="modal-actions">
      <a @click.prevent='close_modal' class='btn btn-cancel' :style="{ marginRight: camera ? '20px' : '0px' }">Cancel</a>
      <a v-if="camera" @click.prevent='select' class='btn btn-primary'>Select</a>
    </div>
  </div>
</template>

<script>
import * as Sentry from '@sentry/browser';
import all_cameras from 'app/cameras.json'

// Raw EXIF "Model" tag => camera, built from the "EXIF Models" column of lib/cameras.csv
// (see docs/cameras.md)
const EXIF_MAP = {}
Object.values(all_cameras).forEach(cameras => {
  cameras.forEach(camera => {
    (camera.exif || []).forEach(exif_model => { EXIF_MAP[exif_model] = camera })
  })
})

export default {
  props: ['supported_brands', 'supported_cameras', 'close_modal'],
  data () {
    return {
      camera: null,
      loading: false,
      highlight: false,
      progress: 0,
      largeFile: false,
      error: null
    }
  },
  methods: {
    drop(event) {
      this.highlight = false
      const file = event.dataTransfer.files[0]
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)

      document.getElementById('js-camera-image').files = dataTransfer.files
      this.submit()
    },
    validate() {
      this.error = null

      if (!this.camera) {
        this.error = 'You must select a photo taken from your camera.'
        return false
      }
    },
    submit() {
      this.error = null
      this.progress = 0
      this.loading = true

      const body = new FormData()
      body.append('file', document.getElementById('js-camera-image').files[0])

      const xhr = new XMLHttpRequest();

      const url = process.env.NODE_ENV === 'production' ? 'https://arsenal-exif-53d86d783590.herokuapp.com/exif' : 'http://localhost:3001/exif'
      xhr.open('POST', url, true)

      xhr.upload.onprogress = (event) => {
        this.progress = Math.round(event.loaded / event.total * 100)
      }

      xhr.onload = () => {
        if (xhr.status == 200) {
          const data = JSON.parse(xhr.response)

          if (!data.model) {
            this.handleError('The uploaded image does not contain exif data.  Please try a different image.')
            return
          }

          // Only offer cameras this Arsenal supports (the result box reads model_formatted from it)
          const exif_camera = EXIF_MAP[data.model]
          this.camera = exif_camera && this.supported_cameras.find(c => c.brand === exif_camera.brand && c.model === exif_camera.model)

          if (!this.camera) {
            console.log(`Camera not found: ${data.make} ${data.model}`)
            Sentry.captureException(`Camera not found: ${data.make} ${data.model}`)
            this.error = `Camera not supported by Arsenal: ${data.make} ${data.model}`
            this.loading = false
            return
          }

          this.loading = false
        } else {
          console.log('What Camera request failed')
          Sentry.captureException('What Camera request failed')
          this.handleError()
        }
      }

      xhr.onerror = (error) => {
        console.log('What Camera request onerror', error)
        Sentry.captureException(error)

        this.handleError()
      }

      xhr.send(body)
    },
    select() {
      this.validate()

      if (this.error) return this.handleError(this.error)
      if (!this.camera) return this.handleError('Please upload a photo taken with your camera.')

      this.$emit('select', this.camera)
    },
    handleError(error = 'Something went wrong.  Please try again or contact support.') {
      this.$toast(error, { horizontalPosition: 'center', verticalPosition: 'top' })
      this.loading = false
    }
  },
};
</script>
